import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { BrandLockup } from "@/components/Brand";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("individual");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await register({ name, email, password, role });
            nav(role === "advisor" ? "/advisor" : "/dashboard", { replace: true });
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="flex items-center justify-center p-6 sm:p-10 order-2 lg:order-1">
                <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="register-form">
                    <div className="label-tag mb-2">Create vault</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Start your life system</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Free for families. Advisors choose the advisor role.</p>

                    <div className="mt-8 space-y-4">
                        <div>
                            <Label>I am a</Label>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                                {["individual", "advisor"].map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        data-testid={`role-${r}`}
                                        onClick={() => setRole(r)}
                                        className={`rounded-md border px-3 py-2 text-sm capitalize transition-colors duration-200 ${
                                            role === r
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "border-border bg-card hover:bg-secondary"
                                        }`}
                                    >
                                        {r === "individual" ? "Family / Self" : "Financial Advisor"}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="name">Full name</Label>
                            <Input
                                id="name" required
                                data-testid="register-name"
                                value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="Aarav Sharma"
                            />
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email" type="email" required
                                data-testid="register-email"
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@family.in"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password" type="password" required minLength={6}
                                data-testid="register-password"
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 6 characters"
                            />
                        </div>
                    </div>

                    {err ? (
                        <div data-testid="register-error" className="mt-4 text-sm text-destructive">{err}</div>
                    ) : null}

                    <Button
                        type="submit" disabled={busy}
                        data-testid="register-submit"
                        className="mt-6 w-full rounded-full"
                    >
                        {busy ? "Creating…" : (<>Create vault <ArrowRight className="ml-2 h-4 w-4" /></>)}
                    </Button>

                    <p className="mt-6 text-sm text-muted-foreground">
                        Already have a vault?{" "}
                        <Link to="/login" data-testid="link-to-login" className="text-primary font-semibold hover:underline">
                            Sign in
                        </Link>
                    </p>
                </form>
            </div>
            <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-bl from-primary via-primary/95 to-[hsl(38_75%_45%)] text-primary-foreground order-1 lg:order-2 relative overflow-hidden">
                <div className="absolute inset-0 grain opacity-50" />
                <Link to="/" className="self-end relative">
                    <BrandLockup size="md" parent={true} className="text-primary-foreground [&_*]:text-primary-foreground" />
                </Link>
                <div className="relative">
                    <h2 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight max-w-md">
                        A vault built today.
                        <br />
                        <span className="italic font-medium opacity-85">A gift unwrapped tomorrow.</span>
                    </h2>
                    <p className="mt-5 text-primary-foreground/85 text-sm max-w-md leading-relaxed">
                        Most platforms track money. LifeVault preserves the life around it.
                    </p>
                </div>
                <div className="relative text-[10px] uppercase tracking-[0.32em] font-semibold text-primary-foreground/70">
                    <a
                        href="https://mythosindiastudio.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="register-mythos-link"
                        className="hover:text-primary-foreground transition-colors"
                    >
                        A MYTHIVA™ platform ↗
                    </a>
                    <span className="mx-2 opacity-60">·</span>
                    India-rooted · AI-first
                </div>
            </div>
        </div>
    );
}
